import React from 'react';
import { useWindowDimensions } from 'react-native';
import { Trans } from '@lingui/macro';
import { useNavigation } from '@react-navigation/native';
import { truncate } from 'lodash';
import { useTheme } from 'styled-components/native';

import Avatar from '@src/components/Avatar';
import Divider from '@src/components/Divider';
import JourneyCard from '@src/components/JourneyCard';
import RefreshControl from '@src/components/RefreshControl';
import Separator from '@src/components/Separator';
import Touchable from '@src/components/Touchable';
import { Body1, H2 } from '@src/components/Typography';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useUserProfile from '@src/hooks/useUserProfile';

import EditProfileButton from './EditProfileButton';
import RelationButton from './RelationButton';
import {
  Background,
  Badge,
  Container,
  Credit,
  Gradient,
  JourneysList,
  RowSection,
  Section,
  Spinner,
  Title,
  UserInfoSection,
} from './styled';
import { ScreenNavigationProp } from './types';
import useUserCredit from './useUserCredit';
import useUserJourneys from './useUserJourneys';

type Props = {
  id: string;
};

const Heading: React.FC<Props> = ({ id }) => {
  const theme = useTheme();
  const dimensions = useWindowDimensions();
  const navigation = useNavigation<ScreenNavigationProp>();
  const userProfile = useUserProfile(id);
  const userCredit = useUserCredit(id);
  const journeys = useUserJourneys(id);
  const {
    tokens: { userID },
  } = useAuthenticationTokens();

  const handleFollowingsPress = React.useCallback(
    () => navigation.push('ProfileFollowings', { id }),
    [id, navigation]
  );

  const handleFollowersPress = React.useCallback(
    () => navigation.push('ProfileFollowers', { id }),
    [id, navigation]
  );

  const isMyProfile = React.useMemo(() => id === userID, [id, userID]);
  const name = truncate(userProfile.data?.name, { length: 15 });
  const username = truncate(userProfile.data?.username, { length: 15 });

  return (
    <Container style={{ width: dimensions.width }}>
      <Background
        source={{
          uri:
            'https://artwork.wallartprints.com/media/catalog/category/mountain-pictures.jpg',
        }}
      >
        <Gradient
          locations={[0.65, 0.9]}
          colors={['rgba(255,255,255,0)', theme.colors.background]}
        />
      </Background>
      <Title>
        <RowSection>
          <Avatar source={userProfile?.data?.avatar} size={82} />
          <Separator />
          <UserInfoSection>
            <H2>{name}</H2>
            <Body1>{username}</Body1>
          </UserInfoSection>
        </RowSection>
        <RowSection>
          {userProfile.loading || !userProfile.data ? (
            <Spinner />
          ) : isMyProfile ? (
            <EditProfileButton profile={userProfile.data} />
          ) : (
            <RelationButton
              profile={userProfile.data}
              onChange={userProfile.refetch}
            />
          )}
        </RowSection>
      </Title>
      <Credit>
        <Section>
          <Badge source={{ uri: userCredit.data?.imageUrl ?? '' }} />
        </Section>
        <Section>
          <Touchable onPress={handleFollowingsPress}>
            <>
              <H2>{userProfile?.data?.followingsCount}</H2>
              <Body1>
                <Trans>Followings</Trans>
              </Body1>
            </>
          </Touchable>
        </Section>
        <Section>
          <Touchable onPress={handleFollowersPress}>
            <>
              <H2>{userProfile?.data?.followersCount}</H2>
              <Body1>
                <Trans>Followers</Trans>
              </Body1>
            </>
          </Touchable>
        </Section>
      </Credit>
      <Separator />
      <Divider />
      <Separator />
      {journeys?.data?.length > 0 && (
        <JourneysList
          data={journeys.data}
          renderItem={({ item }) => <JourneyCard item={item} />}
          onEndReached={journeys.handleEndReached}
          refreshControl={
            <RefreshControl
              refreshing={journeys.refreshing}
              onRefresh={journeys.handleRefresh}
            />
          }
        />
      )}
    </Container>
  );
};

export default React.memo(Heading);
